import { enTetesAuth } from './enTetesAuth';
/**
 * GroupNames Service - Handle group name suggestions and translations
 */


const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/groupnames';

/**
 * Handle API response
 */
const handleResponse = async (response) => {
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
};

/**
 * Get group name suggestions for autosuggest
 * @param {string} keyword - Search keyword
 * @param {string} lang - Current language (fr, en, nl, de, es, it, pt, el)
 * @param {number} limit - Max results
 * @returns {Promise<Array>} List of group name suggestions
 */
export const getGroupNameSuggestions = async (keyword = '', lang = 'fr', limit = 10) => {
    try {
        const params = new URLSearchParams({
            keyword,
            lang,
            limit: limit.toString()
        });

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        // Debug: log raw response
        const responseText = await response.text();
        console.log('GroupNames API raw response:', responseText);

        let result;
        try {
            result = responseText ? JSON.parse(responseText) : {};
        } catch (e) {
            console.error('Failed to parse groupnames response:', e);
            return [];
        }

        console.log('GroupNames API parsed result:', result);
        const data = result.data || result.DATA || [];

        // Normalize and return suggestions with the name in current language
        return data.map(item => {
            const langKey = `name_${lang}`;
            const langKeyLower = langKey.toLowerCase();
            return {
                id: item.id || item.ID,
                name: item[langKeyLower] || item[langKey] || item.name_fr || item.NAME_FR,
                name_fr: item.name_fr || item.NAME_FR,
                name_en: item.name_en || item.NAME_EN,
                name_nl: item.name_nl || item.NAME_NL,
                name_de: item.name_de || item.NAME_DE,
                name_es: item.name_es || item.NAME_ES,
                name_it: item.name_it || item.NAME_IT,
                name_pt: item.name_pt || item.NAME_PT,
                name_el: item.name_el || item.NAME_EL
            };
        });
    } catch (error) {
        console.error('Error getting group name suggestions:', error);
        return [];
    }
};

/**
 * Create a new group name with translations
 * @param {string} name - The group name in current language
 * @param {string} sourceLang - The source language
 * @param {number} createdById - The user ID who created this group name
 * @returns {Promise<Object>} Created group name with translations
 */
export const createGroupNameWithTranslations = async (name, sourceLang = 'fr', createdById = 0) => {
    try {
        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify({
                name,
                sourceLang,
                createdById
            })
        });

        const result = await handleResponse(response);
        console.log('createGroupNameWithTranslations API result:', result);

        if (result.success || result.SUCCESS) {
            // Handle different response formats
            const data = result.data || result.DATA || {};
            return {
                success: true,
                groupNameId: data.id || data.ID || result.groupNameId || result.GROUPNAMEID,
                translations: data.translations || data.TRANSLATIONS || result.translations || result.TRANSLATIONS || {},
                isExisting: result.isExisting || result.ISEXISTING || false
            };
        }

        return { success: false, message: result.message || result.MESSAGE };
    } catch (error) {
        console.error('Error creating group name:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Translate text to all supported languages
 * @param {string} text - Text to translate
 * @param {string} sourceLang - Source language
 * @returns {Promise<Object>} Translations object
 */
export const translateText = async (text, sourceLang = 'fr') => {
    try {
        const response = await fetch(`${API_BASE_URL}/translate`, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify({
                text,
                sourceLang
            })
        });

        const result = await handleResponse(response);

        return {
            success: result.success,
            translations: result.translations || result.TRANSLATIONS || {}
        };
    } catch (error) {
        console.error('Error translating text:', error);
        // Fallback: return the same text for all languages
        return {
            success: false,
            translations: {
                fr: text, en: text, nl: text, de: text,
                es: text, it: text, pt: text, el: text
            }
        };
    }
};
